import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import useMutation from '../../../../../modules/react/hooks/useMutation';
import useCurrentBuilderPage from '../../../../../admin/hooks/useCurrentBuilderPage';
import getPlaceholderMetadata from '../../../../../modules/media/getPlaceholderMetadata';
import { FLIPPER_VOYAGER_MEDIA_GALLERY } from '../../../../../constants/flippers';
import useFlipper from '../../../../../modules/react/hooks/useFlipper';
import withImages from '../../../../../modules/react/editors/ImageWidget/withImages';
import Editor from './Editor';

export const UPLOAD_MEDIUM = gql`
  mutation MediumUpload($file: Upload!) {
    mediumUpload(file: $file) {
      medium {
        id
        decorative
        companyId
        filename
        url
        formattedUpdatedAt
        defaultMetadata {
          title
          altText
        }
      }
      userErrors {
        code
        message
      }
    }
  }
`;

export const UPDATE_MEDIA = gql`
  mutation MediaUpdate($media: [MediumInput!], $locale: String) {
    mediaUpdate(media: $media) {
      media {
        id
        ...FacilityImageSlideShowEditor
      }
      userErrors {
        code
        message
      }
    }
  }

  ${Editor.fragments.media}
`;

const propTypes = {
  clearUpload: PropTypes.func.isRequired,
  facility: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  onCancelEdit: PropTypes.func.isRequired,
  onFinishEdit: PropTypes.func.isRequired,
  updateFacility: PropTypes.func.isRequired,
  uploadV1: PropTypes.func.isRequired,
  editorImages: PropTypes.arrayOf(PropTypes.shape({
    mediumId: PropTypes.string,
    file: PropTypes.string,
  })),
  loadingMedia: PropTypes.bool,
};

const defaultProps = {
  editorImages: [{ file: '/img/grid-4.jpg', alt: 'facility-image' }],
  loadingMedia: false,
};

const EditorContainer = ({
  clearUpload,
  facility,
  onCancelEdit,
  onFinishEdit,
  updateFacility,
  uploadV1,
  editorImages,
  loadingMedia,
  ...rest
}) => {
  const mediaGalleryEnabled = useFlipper(FLIPPER_VOYAGER_MEDIA_GALLERY);

  const [
    uploadImage, { loading, data: uploadData },
  ] = useMutation(UPLOAD_MEDIUM, { refetchQueries: ['GetAllMedia'] });

  const [updateMedia] = useMutation(UPDATE_MEDIA);

  const { locale } = useCurrentBuilderPage();

  if (loadingMedia) {
    return null;
  }

  const onUpload = ({ target: { files: [file] } }) => {
    if (!file) { return; }
    if (mediaGalleryEnabled) {
      uploadImage({ variables: { file } });
    } else { uploadV1(file); }
  };

  const onSave = async ({ images, autoplay, showBanner, bannerText, initialValues }) => {
    clearUpload();

    const cleanedImages = images.map((image) => {
      if (!image.mediumId) { return image; }
      return { mediumId: image.mediumId, link: image.link, target: image.target, file: image.file };
    });

    if (mediaGalleryEnabled) {
      const mediaUpdateImages = images.reduce((acc, img) => {
        const { alt, title, id, decorative, mediumId } = img;
        if (!mediumId) { return acc; }

        const updatedAccumulator = [
          ...acc,
          {
            id,
            decorative,
            metadata: {
              locale,
              altText: alt,
              title,
            },
          },
        ];

        const initialValue = initialValues.find(val => val.mediumId === mediumId);
        if (!initialValue) {
          return updatedAccumulator;
        }

        const {
          altText: initialAlt,
          title: initialTitle,
        } = getPlaceholderMetadata(initialValue, locale);
        if (initialAlt === alt
          && initialTitle === title
          && initialValue.decorative === decorative
        ) {
          // if alt and title are equal to the initial placeholder metadata
          // no updates have been made, do not update it
          return acc;
        }

        return updatedAccumulator;
      }, []);

      updateMedia(
        { variables: { media: mediaUpdateImages, locale } },
      );
    }

    onFinishEdit({
      otherImages: cleanedImages,
      autoplay,
      showBanner,
      bannerText,
      facilityId: facility.id,
    });
  };

  const onCancel = () => {
    clearUpload();
    onCancelEdit();
  };

  return (
    <Editor
      {...rest}
      otherImages={editorImages}
      data={uploadData}
      loading={loading}
      onCancel={onCancel}
      onSave={onSave}
      onUpload={onUpload}
    />
  );
};

EditorContainer.propTypes = propTypes;
EditorContainer.defaultProps = defaultProps;

export default withImages(EditorContainer);
